// @ts-strict-ignore
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import {
  Parties,
  PartiesDropDown,
  EntityMap,
  Entity,
} from '../../models/parties.model';
import { QueryParams } from 'src/app/models/party-selector.model';
import { AdminService } from '../../services/admin.service';
import { SessionService } from '../../services/session.service';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { filter, takeUntil } from 'rxjs/operators';
import { MemberToggleComponent } from '../party-selector/memberToggleDialog/member-toggle-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SessionStorageService } from '../../services/session-storage.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'party-selector',
  templateUrl: './party-selector.component.html',
  styleUrl: './party-selector.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PartySelectorComponent implements OnInit, OnDestroy {
  @ViewChild('partySelect') public partySelect: MatSelect;

  public allPartiesDropDown: PartiesDropDown[] = [];
  public onlyMembers: boolean;
  public partiesDropDown: PartiesDropDown[] = [];
  public membersDropDown: PartiesDropDown[] = [];
  // memberParties is used globally
  public memberParties: Entity[] = [];
  public isFxhpAdmin = false;
  public selectedParty: Entity;
  private pageClosed = new Subject<void>();
  public partyId: string;
  public partyName: string;
  public paramsPartyType: string;
  public partyType: string;
  public displayImage: boolean;
  public displayDropDown: boolean;
  public selectedPartyName: string;
  public imageSource: string;
  public fxhpEntityId: string;
  private readonly activeRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly dialog: MatDialog = inject(MatDialog);

  constructor(
    private readonly adminService: AdminService,
    private readonly sessionService: SessionService,
    private readonly sessionStorageService: SessionStorageService,
  ) {}

  public ngOnInit(): void {
    this.isFxhpAdmin = this.sessionService.isAdmin();
    this.paramsPartyType = sessionStorage.getItem('selectedEntityType');
    this.partyType = sessionStorage.getItem('selectedEntityType');
    /**
     * Will only be hit as an admin
     * @summary
     * When we create a new Member or Credit Bank we need to clear the parties
     * And reinitialize the drop down
     */
    this.adminService.partiesClearedSubscription
      .pipe(takeUntil(this.pageClosed))
      .subscribe(() => {
        this.getParties(false);
      });

    this.getParties(true);

    /**
     * When changing pages will hit the toggle dropdown
     */
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.pageClosed),
      )
      .subscribe(() => {
        if (this.isFxhpAdmin) {
          this.setAdminPartySelector();
        }
      });
  }

  /**
   * @summary
   * Currently non members can only see the Rates, Reports, and Setting Page
   * When the selected entity is a non member we need to revert back to Vangaurd
   *
   * The dropdown toggle as well should only show Members on Member only supported pages (Pools, Credit)
   */

  public getParties(initialized: boolean): void {
    this.adminService
      .getParties()
      .pipe(takeUntil(this.pageClosed))
      .subscribe((allParties: Parties) => {
        // Saves the parties in memory
        this.adminService.setParties(allParties);
        let viewableEntities = 0;
        // Makes an array of all parties (except for FXHP) that the current user has access to in dropdown
        // and assings fxhpEntityId to FXHP id
        this.memberParties = allParties.members.reduce((acc, current) => {
          if (current.entityType === 'fxhp' && current.name === 'FXHP') {
            this.fxhpEntityId = current.id;
            return [...acc];
          } else {
            return [...acc, current];
          }
        }, []);
        this.memberParties.sort((a, b) =>
          a.name.toLowerCase() < b.name.toLowerCase()
            ? -1
            : Number(a.name.toLowerCase() > b.name.toLowerCase()),
        );
        viewableEntities += this.memberParties.length;

        // We save all members this user has access to (I don't know why)
        if (allParties.members) {
          const memberMap: EntityMap = {};
          allParties.members.forEach((member) => {
            memberMap[member.id] = member;
          });
          this.sessionStorageService.set('members', JSON.stringify(memberMap));
        }

        // Create array of credit banks to be displayed in drop down
        const creditBankParties: Entity[] = allParties.creditBanks;
        creditBankParties.sort((a, b) =>
          a.name.toLowerCase() < b.name.toLowerCase()
            ? -1
            : Number(a.name.toLowerCase() > b.name.toLowerCase()),
        );
        viewableEntities += creditBankParties.length;
        if (allParties.creditBanks) {
          const creditMap: EntityMap = {};
          allParties.creditBanks.forEach((creditBank) => {
            creditMap[creditBank.id] = creditBank;
          });
          this.sessionStorageService.set(
            'creditBanks',
            JSON.stringify(creditMap),
          );
        }

        // Create array of intermediaires to be displayed in drop down
        const intermediaryParties: Entity[] = allParties.intermediaries;
        viewableEntities += intermediaryParties.length;
        // We save all intermediaries this user has access to (I don't know why)
        if (allParties.intermediaries) {
          const intermediaryMap: EntityMap = {};
          allParties.intermediaries.forEach((intermediary) => {
            intermediaryMap[intermediary.id] = intermediary;
          });
          this.sessionStorageService.set(
            'intermediaries',
            JSON.stringify(intermediaryMap),
          );
        }

        // This is drop down when we show all entity types
        this.allPartiesDropDown = [
          { groupName: 'Members', parties: this.memberParties },
          { groupName: 'Credit Banks', parties: creditBankParties },
          { groupName: 'Intermediaries', parties: intermediaryParties },
        ];

        this.displayDropDown = viewableEntities > 1;
        // This is drop down when we only show members
        this.membersDropDown = this.allPartiesDropDown.slice(0, 1);

        if (initialized) {
          let memberId: string = null;
          // If the entity id is not FXHP id, set memberid to selectedEntityId
          if (
            this.sessionStorageService.get('selectedEntityId') !==
            this.fxhpEntityId
          ) {
            memberId = this.sessionStorageService.get('selectedEntityId');
          }
          const firstMember =
            this.paramsPartyType === 'credit_bank'
              ? creditBankParties[0].id
              : this.memberParties[0].id;
          memberId = !memberId ? firstMember : memberId;
          this.sessionStorageService.set('selectedEntityId', memberId);

          const queryParams = { id: memberId, partyType: this.paramsPartyType };
          this.updateQueryParameters(queryParams);
          if (this.partyType === 'fxhp' || this.partyType === 'member') {
            this.selectedParty = this.memberParties.find(
              (memberParty) => memberParty.id === memberId,
            );
          } else if (this.partyType === 'credit_bank') {
            this.selectedParty = creditBankParties.find(
              (creditBank) => creditBank.id === memberId,
            );
          } else {
            this.selectedParty = intermediaryParties.find(
              (creditBank) => creditBank.id === memberId,
            );
          }
          this.partyName = this.selectedParty.name;
          this.selectedPartyName = this.selectedParty.name;
          this.setEntitySessionProperties();
          if (this.partyType === 'member') {
            this.partiesDropDown = this.allPartiesDropDown.slice(0, 1);
          } else if (this.partyType === 'credit_bank') {
            this.partiesDropDown = this.allPartiesDropDown.slice(1, 2);
          }
          this.findImageSource();
        }
        if (this.isFxhpAdmin) {
          this.setAdminPartySelector();
        }
      });
  }

  /**
   * @summary
   * New Party Selected is bound to the HTML dropdown.
   * If a new entity is selected it will update the session storage with the entity properties
   * It will as well update the query parameters with entityId and entityType
   */

  public newPartySelected(select: MatSelectChange): void {
    const { value } = select;
    const oldPartyName = this.partyName;
    let id = value.id;
    let partyType = 'credit_bank';
    if (value.entityType === 'member') {
      id = value.id;
      partyType = 'member';
    } else if (value.entityType === 'intermediary') {
      id = value.id;
      partyType = 'intermediary';
    }
    this.partyName = value.name;
    this.updateQueryParameters({ id, partyType });
    this.selectedPartyName = value.name;
    this.findImageSource();
    this.sessionStorageService.set('selectedEntityType', this.partyType);
    this.sessionStorageService.set('selectedEntityId', this.partyId.toString());
    this.setEntitySessionProperties();
    if (!this.isFxhpAdmin) {
      this.dialog.open(MemberToggleComponent, {
        width: '407px',
        height: '268px',
        closeOnNavigation: true,
        data: {
          oldPartyName: oldPartyName,
          newPartyName: value.name,
        },
      });
    }
  }

  /**
   * @summary
   * Setup FXHP Admin party selector.
   */

  public setPartySelectorToFirstMember(): void {
    this.paramsPartyType = this.sessionService.isCreditBank()
      ? 'credit_bank'
      : 'member';
    const firstMember = this.memberParties[0].id;
    this.sessionStorageService.set('selectedEntityId', firstMember);

    const queryParams = { id: firstMember, partyType: this.paramsPartyType };
    this.updateQueryParameters(queryParams);

    this.selectedParty = this.memberParties.find(
      (memberParty: Entity) => memberParty.id === firstMember,
    );
    this.partyName = this.selectedParty.name;
    this.selectedPartyName = this.selectedParty.name;
    this.sessionStorageService.set('selectedEntityType', this.partyType);
    this.setEntitySessionProperties();
    this.findImageSource();
  }

  public setAdminPartySelector(): void {
    if (this.sessionStorageService.get('selectedEntityId') === null) {
      this.sessionStorageService.set('selectedEntityId', this.selectedParty.id);
    }

    const onlyMembers =
      this.router.url.indexOf('reports') === -1 &&
      this.router.url.indexOf('/credit') === -1 &&
      this.router.url.indexOf('admin-blotter') === -1;
    if (onlyMembers && this.partyType !== 'member') {
      this.setPartySelectorToFirstMember();
    }

    this.partiesDropDown = onlyMembers
      ? this.membersDropDown.slice(0)
      : this.allPartiesDropDown.slice(0);

    if (
      !onlyMembers &&
      (this.router.url.indexOf('admin-blotter') > -1 ||
        this.router.url.indexOf('/credit') > -1)
    ) {
      if (sessionStorage.getItem('selectedEntityType') === 'intermediary') {
        this.setPartySelectorToFirstMember();
      }
      this.partiesDropDown = this.allPartiesDropDown.slice(0, 2);
    }

    if (!onlyMembers && this.router.url.indexOf('reports') > -1) {
      this.partiesDropDown = this.allPartiesDropDown.slice(0);
    }
  }

  /**
   * @summary
   * Find Image source is used by all entities to display that entities icon.
   */
  public findImageSource(): void {
    this.displayImage = true;
    this.imageSource = `${environment.appHost}/${this.partyType}/${this.partyName}`;
    fetch(this.imageSource, { method: 'GET', mode: 'no-cors' })
      .then()
      .catch(() => {
        this.displayImage = false;
      });
  }

  public trackByIndex(index: number): number {
    return index;
  }

  /**
   * @summary
   * Toggle Select is bound to the HTML element representing the member capsule.
   * If it is clicked it will expand the dropdown menu
   */
  public toggleSelect(): void {
    this.partySelect.toggle();
  }

  /**
   * @summary
   * Updates Query Parameters.
   */
  private updateQueryParameters(queryParams: QueryParams): void {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
    this.partyType = queryParams.partyType;
    this.partyId = queryParams.id;
  }

  public setEntitySessionProperties(): void {
    this.sessionStorageService.set('memberName', this.selectedParty.name);
    this.sessionStorageService.set(
      'memberSendsAllocation',
      this.selectedParty.memberSendsAllocation.toString(),
    );
    this.sessionStorageService.set(
      'canViewUploadButton',
      this.selectedParty.canViewUploadButton.toString(),
    );
    this.sessionStorageService.set(
      'canViewOrderEntryButton',
      this.selectedParty.canViewOrderEntryButton.toString(),
    );
  }

  public ngOnDestroy(): void {
    this.pageClosed.next();
    this.pageClosed.complete();
  }
}
